// AI Service Layer - Handles communication with different AI providers
import authService from './authService';

class AiService {
  constructor(apiBaseUrl, provider = 'ollama') {
    this.apiBaseUrl = apiBaseUrl;
    this.provider = provider;
  }

  setProvider(provider) {
    this.provider = provider;
  }

  getProvider() {
    return this.provider;
  }

  // Create a new conversation
  async createConversation(userId) {
    try {
      const userIdStr = typeof userId === 'string' ? userId : String(userId);
      const response = await fetch(`${this.apiBaseUrl}/chat/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify({ user_id: userIdStr })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create conversation: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      return data.conversation_id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  // Fetch conversations for a user
  async fetchConversations(userId) {
    try {
      const userIdStr = typeof userId === 'string' ? userId : String(userId);
      const response = await fetch(`${this.apiBaseUrl}/chat/conversations?user_id=${encodeURIComponent(userIdStr)}`, {
        method: 'GET',
        headers: {
          ...authService.getAuthHeaders()
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.status}`);
      }
      
      const data = await response.json();
      return data.conversations;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  }

  // Load messages for a specific conversation
  async loadConversationMessages(conversationId) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/chat/conversations/${conversationId}`, {
        method: 'GET',
        headers: {
          ...authService.getAuthHeaders()
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load conversation messages: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Convert messages to the format expected by the UI
      const formattedMessages = data.messages.map(msg => ({
        type: msg.role === 'user' ? 'user' : 'ai',
        text: msg.content
      }));
      
      return formattedMessages;
    } catch (error) {
      console.error('Error loading conversation messages:', error);
      throw error;
    }
  }

  // Send a message (non-streaming)
  async sendMessage(conversationId, message, userId) {
    try {
      const messageUrl = this.provider === 'ollama' 
        ? `${this.apiBaseUrl}/chat/conversations/${conversationId}/messages/ollama`
        : `${this.apiBaseUrl}/chat/conversations/${conversationId}/messages`;
      
      const userIdStr = typeof userId === 'string' ? userId : String(userId);
      const response = await fetch(messageUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          message: message,
          user_id: userIdStr
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to send message: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      return {
        message: data.message || data.response,
        thinkProcess: data.think_process,
        titleUpdated: data.title_updated
      };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  // Send a streaming message
  async sendStreamingMessage(conversationId, message, onChunk, onComplete, onError, userId) {
    try {
      const streamUrl = this.provider === 'ollama' 
        ? `${this.apiBaseUrl}/chat/conversations/${conversationId}/messages/ollama/stream`
        : `${this.apiBaseUrl}/chat/conversations/${conversationId}/messages/stream`;
      
      const userIdStr = typeof userId === 'string' ? userId : String(userId);
      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify({ message: message, user_id: userIdStr })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to start streaming: ${response.status}`);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'chunk') {
                onChunk(data.chunk);
              } else if (data.type === 'complete') {
                onComplete();
                return;
              } else if (data.type === 'error') {
                throw new Error(data.error);
              }
            } catch (parseError) {
              console.error('Error parsing streaming data:', parseError);
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      onError(error);
    }
  }

  // Update conversation title
  async updateConversationTitle(conversationId, title) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/chat/conversations/${conversationId}/title`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify({ title })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update conversation title: ${response.status}`);
      }
      
      return true;
    } catch (error) {
      console.error('Error updating conversation title:', error);
      throw error;
    }
  }

  // Delete a conversation
  async deleteConversation(conversationId) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/chat/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: {
          ...authService.getAuthHeaders()
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete conversation: ${response.status}`);
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }
}

export default AiService;